import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import RecipeCard from '../components/RecipeCard';
import Masonry from 'react-masonry-css';
import { Spinner } from 'react-bootstrap';
import { useGetAllFavoriteRecipes } from '../hooks/favoriteRecipe';
import { useGetAllDislikedRecipes } from '../hooks/dislikeRecipe';
import { fetchWithCSRF } from '../utils/csrfUtils';
import supabase from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import ErrorBoundary from '../components/ErrorBoundary';

const RecipesForTag = ({ tag: propTag, searchTerm = '', onMealAdded }) => {
    const location = useLocation();
    const { tagName } = useParams();
    // Use prop tag if provided, otherwise fall back to location state (for backward compatibility)
    const tag = propTag || location.state?.tag;
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const { getAllFavoriteRecipes, isLoading: isLoadingFavorites, error: errorFavorites } = useGetAllFavoriteRecipes();
    const { getAllDislikedRecipes } = useGetAllDislikedRecipes();
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const [dislikedRecipes, setDislikedRecipes] = useState([]);
    const [pantryIngredients, setPantryIngredients] = useState(null);

    const { sessionToken, pgUser } = useAuth();

    const breakpointColumns = {
        default: 4,
        1100: 3,
        700: 2,
        400: 1
    };

    useEffect(() => {
        if (!sessionToken) return;
        
        const fetchFavoritesAndDislikes = async () => {
            try {
                // Fetch favorites
                const favoritesData = await getAllFavoriteRecipes(sessionToken);
                if (Array.isArray(favoritesData)) {
                    setFavoriteRecipes(favoritesData);
                }

                // Fetch dislikes
                const dislikesData = await getAllDislikedRecipes(sessionToken);
                if (Array.isArray(dislikesData)) {
                    setDislikedRecipes(dislikesData);
                }
            } catch (error) {
                console.error('Error fetching favorites/dislikes:', error);
            }
        };
        fetchFavoritesAndDislikes();
    }, [sessionToken, getAllFavoriteRecipes, getAllDislikedRecipes]);

    // Fetch pantry ingredients for pantry match calculation
    useEffect(() => {
        const fetchPantryIngredients = async () => {
            if (!sessionToken || !pgUser?.pantry_id || !pgUser?.id) return;
            
            try {
                const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pgUser.pantry_id}/app_user/${pgUser.id}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        setPantryIngredients(data);
                    }
                }
            } catch (error) {
                console.error('Error fetching pantry ingredients:', error);
                // Silently fail - pantry match just won't show
            }
        };

        fetchPantryIngredients();
    }, [sessionToken, pgUser?.pantry_id, pgUser?.id]);

    useEffect(() => {
        const fetchRecipes = async () => {
            if (!sessionToken || !tag) {
                setLoading(false);
                return;
            }

            console.log('fetching recipes for tag:', tag.id);

            try {
                const response = await fetch(`/api/v1/recipe-tag/tag/${tag.id}/with-ingredients`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                const json = await response.json();

                if (!response.ok) {
                    throw new Error(`Failed to fetch recipes: ${response.status}`);
                }

                if (Array.isArray(json)) {
                    setRecipes(json);
                } else {
                    console.error('Invalid data format:', json);
                    setRecipes([]);
                }
            } catch (error) {
                console.error('Error fetching recipes:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRecipes();
    }, [tag, sessionToken]);

    const filteredRecipes = recipes.filter(recipe => {
        const searchLower = searchTerm.toLowerCase();
        
        // Search by recipe name
        if (recipe.name.toLowerCase().includes(searchLower)) {
            return true;
        }
        
        // Search by ingredient names
        if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
            return recipe.ingredients.some(ingredient => 
                ingredient.ingredient_name && 
                ingredient.ingredient_name.toLowerCase().includes(searchLower)
            );
        }
        
        return false;
    });

    return (
        <ErrorBoundary fallbackMessage="Unable to load recipes explorer. Please try again later.">
            <div className="container">
                <h2 style={{ textAlign: 'left', paddingLeft: '15px' }}>{tag?.name || tagName} Recipes</h2>
                {tag && <p style={{ textAlign: 'left', paddingLeft: '15px' }}>{tag.description}</p>}
                
                {loading ? (
                    <div className="text-center my-4">
                        <Spinner animation="border" role="status" style={{ color: '#52cd92' }}>
                            <span className="visually-hidden">Loading recipes...</span>
                        </Spinner>
                    </div>
                ) : !tag ? (
                    <div className="alert alert-warning" role="alert">
                        <p className="mb-0">Tag information not available. Please try accessing this page through the tag selection page.</p>
                    </div>
                ) : filteredRecipes.length === 0 && searchTerm ? (
                    <div className="text-center my-5">No recipes found matching "{searchTerm}"</div>
                ) : recipes.length === 0 ? (
                    <div className="alert alert-info" role="alert">
                        {!loading && <p className="mb-0">No recipes found for this tag.</p>}
                    </div>
                ) : (
                    <>
                        <Masonry
                            breakpointCols={breakpointColumns}
                            className="my-masonry-grid"
                            columnClassName="my-masonry-grid_column"
                        >
                            {filteredRecipes.map(recipe => (
                                <RecipeCard 
                                    key={recipe.id} 
                                    recipe={recipe} 
                                    isFavorite={Array.isArray(favoriteRecipes) && favoriteRecipes.includes(Number(recipe.id))}
                                    isDisliked={Array.isArray(dislikedRecipes) && dislikedRecipes.includes(Number(recipe.id))}
                                    pantryIngredients={pantryIngredients}
                                    onMealAdded={onMealAdded}
                                />
                            ))}
                        </Masonry>
                    </>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default RecipesForTag;

